/**
 * WebP support detection and fallback utilities
 */

/**
 * Check if WebP is supported in the current browser
 */
export const isWebPSupported = (): boolean => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  } catch (_error) {
    return false;
  }
};

/**
 * Get the best image format for the current browser
 * Returns 'webp' if supported, 'jpeg' as fallback
 */
export const getBestImageFormat = (): "webp" | "jpeg" => {
  return isWebPSupported() ? "webp" : "jpeg";
};

/**
 * Get the MIME type for the specified format
 */
export const getMimeType = (format: "webp" | "jpeg"): string => {
  return format === "webp" ? "image/webp" : "image/jpeg";
};

/**
 * Get the optimal quality setting for the specified format
 */
export const getOptimalQuality = (format: "webp" | "jpeg"): number => {
  return format === "webp" ? 0.8 : 0.85;
};

/**
 * Get the file extension for the specified format
 */
export const getFileExtension = (format: "webp" | "jpeg"): string => {
  return format === "webp" ? "webp" : "jpg";
};

/**
 * Check if the browser supports a specific image format
 */
export const supportsImageFormat = (format: string): boolean => {
  switch (format.toLowerCase()) {
    case "webp":
      return isWebPSupported();
    case "jpeg":
    case "jpg":
      return true; // JPEG is universally supported
    case "png":
      return true; // PNG is universally supported
    default:
      return false;
  }
};
